import React from 'react'
import i18n from '@pureartisan/simple-i18n'
import shallow from 'zustand/shallow'
import Chart from './Chart'
import LegendChartHeaders from '../Legend/LegendChartHeaders'
import { makeStyles } from '@material-ui/core/styles'
import useStore from '../store'
import clsx from 'clsx'
import {
  DEMOS_BY_COI,
  STATES,
} from './../../../../constants/map'
import {
  IconButton,
  Portal,
  FormControlLabel,
  Box,
  Button,
  Typography,
} from '@material-ui/core'
import { Close } from '@material-ui/icons'
import { SwitchControl } from '../Controls'
import _ from 'lodash'
import { getLongPopName, getMedPopName } from '../utils'
import DemoChip from '../DemoChip/DemoChip'
import Tooltip from '../Tooltip'

const useStyles = makeStyles(theme => ({
  overlayControl: {},
  root: {},
  panelChart: {
    // padding: '25px 20px 60px',
    boxSizing: 'border-box',
    padding: theme.spacing(1, 0),
    width: theme.extras.chart.width,
    zIndex: theme.extras.chart.zIndex,
    background: 'white',
    boxShadow: theme.shadows[1],
    borderRadius: '4px',
  },
  section: {
    padding: `${theme.spacing(1)}px ${theme.spacing(2)}px`,
    '&.headers': {
      // paddingBottom: theme.spacing(2),
      paddingTop: theme.spacing(2),
      borderBottom: theme.extras.styles.faintBorder,
    },
    '&.legend': {
      paddingTop: 0,
    },
  },
  demos: {
    display: 'flex',
    justifyContent: 'center',
    flexWrap: 'wrap',
    '& .tooltip-children-wrapper': {
      marginBottom: theme.spacing(0.5),
      '&:not(:last-child)': {
        marginRight: theme.spacing(0.5),
      },
    },
    '& button': {
      border: theme.extras.styles.faintBorder,
      borderRadius: 8,
      padding: theme.spacing(0.5, 1),
      '&.noData': {
        background:
          theme.extras.variables.colors
            .ddkYetAnotherAnotherLightGray,
      },
    },
    '& .demo-chip': {
      '&.active .demo-name': {
        color: theme.extras.variables.colors.textDark,
      },
      '& .demo-icon': {
        paddingRight: theme.spacing(0.75),
      },
    },
  },
  dataNote: {
    fontStyle: 'italic',
    paddingTop: theme.spacing(1)
  },
  close: {
    position: 'absolute',
    top: '10px',
    right: '10px',
  },
}))

const ChartContainer = ({
  data,
  title,
  subtitle,
  barOptions = DEMOS_BY_COI,
  activeBars,
  handleActivePointLayers,
  handleClose,
  id,
  className,
  children,
}) => {
  const showNote = activeBars.some(demo =>
    data.every(d => !d[demo]),
  )

  const classes = useStyles()

  return (
    <Portal>
      <Box
        id={id}
        className={clsx(classes.panelChart, className)}
      >
        <Box className={clsx(classes.section, 'headers')}>
          <IconButton
            aria-label={i18n.translate(`MENU_CLOSE`)}
            className={clsx(
              classes.close,
              'screenshot-ignore',
            )}
            onClick={handleClose}
          >
            <Close />
          </IconButton>
          <LegendChartHeaders
            title={title}
            subtitle={subtitle}
          />
        </Box>
        <Box className={clsx(classes.section, 'chart')}>
          <Chart
            data={data}
            activeBars={activeBars}
            barOptions={barOptions}
          />
        </Box>
        <Box className={clsx(classes.section, 'legend')}>
          <Box className={clsx(classes.demos)}>
            {barOptions.map(demo => {
              const active = activeBars.some(
                layer => layer === demo,
              )
              const clickHandler = () =>
                handleActivePointLayers(demo)
              const noData = data.every(d => !d[demo])
              return (
                <Tooltip
                  key={demo}
                  interactive={false}
                  title={i18n.translate(
                    `CHART_LEGEND_TOOLTIP${
                      noData ? '_UNAVAILABLE' : ''
                    }`,
                    {
                      demo: getLongPopName(demo),
                      toggle: i18n.translate(
                        `TOGGLE_${active}`,
                      ),
                    },
                  )}
                >
                  <Button
                    onClick={clickHandler}
                    disabled={noData}
                    className={clsx('chart-demo-button', {
                      noData,
                    })}
                  >
                    <DemoChip
                      demo={demo}
                      nameVariant="body2"
                      nameFormatter={getMedPopName}
                      inactive={!active || noData}
                      className={clsx({ active })}
                    />
                  </Button>
                </Tooltip>
              )
            })}
          </Box>
          {showNote && (
          <Box>
            <Typography
              variant="body2"
              color="textSecondary"
              className={classes.dataNote}
              dangerouslySetInnerHTML={{
                __html: i18n.translate(
                  `WKKF_CHART_DATA_NOTE`,
                ),
              }}
            />
          </Box>
          )}
        </Box>
        {children}
      </Box>
    </Portal>
  )
}

ChartContainer.defaultProps = {}

export default ChartContainer
